import { useState } from "react";
import { toast } from "sonner";
import { Download, Loader2 } from "lucide-react";
import { api } from "@/lib/api";
import { useCompany } from "@/context/CompanyContext";
import { PageHeader } from "@/components/PageHeader";
import { EmptyState } from "@/components/EmptyState";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Select, SelectTrigger, SelectValue, SelectContent, SelectItem } from "@/components/ui/select";
import { Table, TableHeader, TableBody, TableRow, TableHead, TableCell } from "@/components/ui/table";
import { fmtNum, todayISO } from "@/lib/format";
import { getErrorMessage } from "@/lib/errors";

const TABS = [
  { key: "trial_balance", label: "Trial Balance" },
  { key: "profit_and_loss", label: "Profit & Loss" },
  { key: "balance_sheet", label: "Balance Sheet" },
  { key: "cash_bank_summary", label: "Cash & Bank" },
];

export default function FinancialStatements() {
  const { companies } = useCompany() || {};
  const [tab, setTab] = useState("trial_balance");
  const [companyId, setCompanyId] = useState(companies?.[0]?.id || "");
  const [asOf, setAsOf] = useState(todayISO());
  const [dateFrom, setDateFrom] = useState(todayISO().slice(0, 8) + "01");
  const [dateTo, setDateTo] = useState(todayISO());
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(false);
  const [exporting, setExporting] = useState(false);

  const effectiveCompanyId = companyId || companies?.[0]?.id;

  const load = async () => {
    if (!effectiveCompanyId) return;
    setLoading(true);
    try {
      const endpoint = { trial_balance: "trial-balance", profit_and_loss: "profit-and-loss", balance_sheet: "balance-sheet", cash_bank_summary: "cash-bank-summary" }[tab];
      const params = tab === "profit_and_loss" ? { company_id: effectiveCompanyId, date_from: dateFrom, date_to: dateTo } : { company_id: effectiveCompanyId, as_of: asOf };
      const { data } = await api.get(`/reports/${endpoint}`, { params });
      setData(data);
    } catch (err) {
      toast.error(getErrorMessage(err));
    } finally {
      setLoading(false);
    }
  };

  const exportReport = async (format) => {
    setExporting(true);
    try {
      const response = await api.post(
        "/reports/export",
        { report_key: tab === "cash_bank_summary" ? "cash_bank_summary" : tab, format, company_id: effectiveCompanyId, as_of: asOf, date_from: dateFrom, date_to: dateTo },
        { responseType: "blob" }
      );
      const url = URL.createObjectURL(response.data);
      const a = document.createElement("a");
      a.href = url;
      a.download = `${tab}.${format === "excel" ? "xlsx" : "pdf"}`;
      a.click();
      URL.revokeObjectURL(url);
    } catch (err) {
      toast.error(getErrorMessage(err));
    } finally {
      setExporting(false);
    }
  };

  return (
    <div className="space-y-6" data-testid="financial-statements-page">
      <PageHeader
        title="Financial Statements"
        description="Trial Balance, Profit & Loss, Balance Sheet and Cash & Bank — per company, as of a date."
        action={
          <div className="flex items-center gap-2">
            <Select value={companyId || companies?.[0]?.id || ""} onValueChange={setCompanyId}>
              <SelectTrigger className="w-48" data-testid="financials-company-select"><SelectValue placeholder="Company" /></SelectTrigger>
              <SelectContent>{(companies || []).map((c) => <SelectItem key={c.id} value={c.id}>{c.name}</SelectItem>)}</SelectContent>
            </Select>
            {tab === "profit_and_loss" ? (
              <>
                <Input type="date" value={dateFrom} onChange={(e) => setDateFrom(e.target.value)} className="w-36" data-testid="financials-date-from-input" />
                <Input type="date" value={dateTo} onChange={(e) => setDateTo(e.target.value)} className="w-36" data-testid="financials-date-to-input" />
              </>
            ) : (
              <Input type="date" value={asOf} onChange={(e) => setAsOf(e.target.value)} className="w-36" data-testid="financials-as-of-input" />
            )}
            <Button onClick={load} disabled={loading} data-testid="financials-run-button">{loading ? <Loader2 className="h-4 w-4 animate-spin" /> : "Run"}</Button>
          </div>
        }
      />

      <div className="flex flex-wrap gap-1.5">
        {TABS.map((t) => (
          <Button key={t.key} size="sm" variant={tab === t.key ? "default" : "outline"} onClick={() => { setTab(t.key); setData(null); }} data-testid={`tab-${t.key}-button`}>
            {t.label}
          </Button>
        ))}
        {data && tab !== "cash_bank_summary" && (
          <div className="ml-auto flex gap-1.5">
            <Button size="sm" variant="outline" disabled={exporting} onClick={() => exportReport("excel")} data-testid="export-excel-button"><Download className="h-3.5 w-3.5" /> Excel</Button>
            <Button size="sm" variant="outline" disabled={exporting} onClick={() => exportReport("pdf")} data-testid="export-pdf-button"><Download className="h-3.5 w-3.5" /> PDF</Button>
          </div>
        )}
      </div>

      {!data ? (
        <EmptyState title="Run a statement" description="Pick a company and date, then click Run." testId="financials-empty-state" />
      ) : tab === "trial_balance" ? (
        <TrialBalanceView data={data} />
      ) : tab === "profit_and_loss" ? (
        <ProfitAndLossView data={data} />
      ) : tab === "balance_sheet" ? (
        <BalanceSheetView data={data} />
      ) : (
        <CashBankView data={data} />
      )}
    </div>
  );
}

function TrialBalanceView({ data }) {
  return (
    <div className="rounded-xl border border-border bg-card" data-testid="trial-balance-view">
      {!data.balanced && (
        <div className="border-b border-border bg-rose-500/10 px-4 py-2 text-xs font-medium text-rose-500" data-testid="trial-balance-unbalanced-warning">
          Trial balance does not balance — please investigate before relying on this report.
        </div>
      )}
      <Table>
        <TableHeader><TableRow><TableHead>Code</TableHead><TableHead>Account</TableHead><TableHead>Type</TableHead><TableHead className="text-right">Debit</TableHead><TableHead className="text-right">Credit</TableHead></TableRow></TableHeader>
        <TableBody>
          {data.rows.map((r) => (
            <TableRow key={r.account_code}>
              <TableCell className="font-mono text-xs">{r.account_code}</TableCell>
              <TableCell className="text-foreground">{r.account_name}</TableCell>
              <TableCell className="text-xs text-muted-foreground">{r.account_type}</TableCell>
              <TableCell className="text-right font-mono">{fmtNum(r.debit, 2)}</TableCell>
              <TableCell className="text-right font-mono">{fmtNum(r.credit, 2)}</TableCell>
            </TableRow>
          ))}
          <TableRow className="font-semibold"><TableCell colSpan={3}>Total</TableCell><TableCell className="text-right font-mono" data-testid="trial-balance-total-debit">{fmtNum(data.total_debit, 2)}</TableCell><TableCell className="text-right font-mono" data-testid="trial-balance-total-credit">{fmtNum(data.total_credit, 2)}</TableCell></TableRow>
        </TableBody>
      </Table>
    </div>
  );
}

function ProfitAndLossView({ data }) {
  return (
    <div className="space-y-4" data-testid="profit-and-loss-view">
      <Section title="Income" rows={data.income} total={data.total_income} />
      <Section title="Expense" rows={data.expense} total={data.total_expense} />
      <div className="rounded-xl border border-border bg-card p-4 text-right">
        <span className="text-sm text-muted-foreground">Net Profit: </span>
        <span className="font-mono text-lg font-semibold text-foreground" data-testid="net-profit-value">₹{fmtNum(data.net_profit, 2)}</span>
      </div>
    </div>
  );
}

function BalanceSheetView({ data }) {
  return (
    <div className="space-y-4" data-testid="balance-sheet-view">
      {!data.balanced && (
        <div className="rounded-xl border border-rose-500/30 bg-rose-500/10 px-4 py-2 text-xs font-medium text-rose-500" data-testid="balance-sheet-unbalanced-warning">
          Balance sheet does not balance — difference ₹{fmtNum(data.difference, 2)}.
        </div>
      )}
      <Section title="Assets" rows={data.assets} total={data.total_assets} />
      <Section title="Liabilities" rows={data.liabilities} />
      <Section title="Equity" rows={data.equity} />
      <div className="rounded-xl border border-border bg-card p-4 flex justify-between text-sm">
        <span className="text-muted-foreground">Total Liabilities + Equity (incl. net income to date)</span>
        <span className="font-mono font-semibold text-foreground" data-testid="balance-sheet-total-liab-equity">₹{fmtNum(data.total_liabilities_and_equity, 2)}</span>
      </div>
    </div>
  );
}

function CashBankView({ data }) {
  return (
    <div className="rounded-xl border border-border bg-card" data-testid="cash-bank-view">
      <Table>
        <TableHeader><TableRow><TableHead>Code</TableHead><TableHead>Account</TableHead><TableHead className="text-right">Balance</TableHead></TableRow></TableHeader>
        <TableBody>
          {data.accounts.map((r) => (
            <TableRow key={r.account_code}><TableCell className="font-mono text-xs">{r.account_code}</TableCell><TableCell className="text-foreground">{r.account_name}</TableCell><TableCell className="text-right font-mono">{fmtNum(r.balance, 2)}</TableCell></TableRow>
          ))}
          <TableRow className="font-semibold"><TableCell colSpan={2}>Total</TableCell><TableCell className="text-right font-mono">{fmtNum(data.total, 2)}</TableCell></TableRow>
        </TableBody>
      </Table>
    </div>
  );
}

function Section({ title, rows, total }) {
  return (
    <div className="rounded-xl border border-border bg-card">
      <div className="border-b border-border px-4 py-2"><h3 className="text-sm font-semibold text-foreground">{title}</h3></div>
      {rows.length === 0 ? (
        <p className="px-4 py-3 text-xs text-muted-foreground">No entries.</p>
      ) : (
        <Table>
          <TableBody>
            {rows.map((r) => (
              <TableRow key={r.account_code}><TableCell className="font-mono text-xs w-24">{r.account_code}</TableCell><TableCell className="text-foreground">{r.account_name}</TableCell><TableCell className="text-right font-mono">{fmtNum(r.amount, 2)}</TableCell></TableRow>
            ))}
            {total !== undefined && (
              <TableRow className="font-semibold"><TableCell colSpan={2}>Total</TableCell><TableCell className="text-right font-mono">{fmtNum(total, 2)}</TableCell></TableRow>
            )}
          </TableBody>
        </Table>
      )}
    </div>
  );
}
